import { Tooltip, Typography } from '@mui/material';
import { useMemo } from 'react';

import { useTranslation } from 'modules/i18n/hooks/useTranslation';
import { Checkbox } from 'modules/common/components/Checkbox';
import { SubchainSelectors } from 'domains/projects/components/ChainCellWithSubchains/SubchainSelectors';
import { mapProjectChains } from 'domains/projects/screens/ProjectPage/components/ProjectChainsTable/hooks/useProjectChains';
import { useAppSelector } from 'store/useAppSelector';
import { selectAllPathsByChainId } from 'modules/chains/store/selectors';
import { getDisabledChainTooltipText } from 'modules/chains/utils/getDisabledChainTooltipText';

import { chainProjectItemTranslation } from '../../translation';
import { useChainProjectAccordionStyles } from '../ChainProjectAccordion/useChainProjectAccordionStyles';
import { useChainProjectAccordion } from '../ChainProjectAccordion/useChainProjectAccordion';
import { IChainProjectAccordionProps } from '../ChainProjectAccordion';

export const ChainProjectSingleItem = ({
  chain,
  handleProjectChange,
  isLoadingAddChainsToProject,
  onToggleAccordion,
  project,
  selectedProjectsSubchains,
  setSelectedSubchains,
}: IChainProjectAccordionProps) => {
  const allChainPaths = useAppSelector(state =>
    selectAllPathsByChainId(state, chain.id),
  );

  const { name, userEndpointToken } = project;

  const {
    isIndeterminate,
    isSelected,
    onProjectChange,
    selectedChainPaths,
    setSelectedChainPaths,
  } = useChainProjectAccordion({
    chain,
    handleProjectChange,
    onToggleAccordion,
    userEndpointToken,
    selectedProjectsSubchains,
    setSelectedSubchains,
  });

  const isProjectSelectedOrIndeterminate = isSelected || isIndeterminate;

  const isLastPathSelected =
    isProjectSelectedOrIndeterminate && selectedChainPaths.length === 1;

  const isLastChainInProject = useMemo(
    () =>
      isProjectSelectedOrIndeterminate &&
      selectedProjectsSubchains[userEndpointToken]?.length <=
        allChainPaths.length &&
      selectedProjectsSubchains[userEndpointToken]?.every(path =>
        allChainPaths.includes(path),
      ),
    [
      allChainPaths,
      isProjectSelectedOrIndeterminate,
      selectedProjectsSubchains,
      userEndpointToken,
    ],
  );

  const mappedProjectChain = useMemo(
    () => mapProjectChains(chain, true),
    [chain],
  );

  const shouldDisableCheckbox = isLastPathSelected || isLastChainInProject;

  const { classes } = useChainProjectAccordionStyles();

  const { keys, t } = useTranslation(chainProjectItemTranslation);

  const renderProjectCheckbox = (
    <Tooltip
      placement="left"
      title={getDisabledChainTooltipText(shouldDisableCheckbox)}
    >
      <div className={classes.projectItemWrapper}>
        <Checkbox
          isDisabled={isLoadingAddChainsToProject || shouldDisableCheckbox}
          hasPadding
          isChecked={isSelected}
          isIndeterminate={isIndeterminate}
          label={name || t(keys.defaultName)}
          onChange={onProjectChange}
          classNameLabel={classes.projectLabel}
        />
      </div>
    </Tooltip>
  );

  if (allChainPaths.length === 1) {
    return renderProjectCheckbox;
  }

  return (
    <>
      {renderProjectCheckbox}
      <Typography variant="subtitle3" color="textSecondary">
        {t(keys.networks)}
      </Typography>
      <SubchainSelectors
        isLastPathSelected={isLastPathSelected}
        isLoading={isLoadingAddChainsToProject}
        chain={mappedProjectChain}
        setSelectedChainPaths={setSelectedChainPaths}
        selectedChainPaths={selectedChainPaths}
        isLastChainInProject={isLastChainInProject}
      />
    </>
  );
};
